import React, { useState } from 'react'

import {
  Body2,
  Box,
  Heading3,
  Heading5,
  Join,
  Space,
  Color,
  Icon,
  IconVariant,
  withCitrusTheme,
  AlignItems,
  JustifyContent,
} from '@gousto-internal/citrus-react'
import moment from 'moment'

import OrderSummary from '../../../containers/welcome/OrderSummary'
import { useWelcomeTracking } from '../hooks/useWelcomeTracking'

type YourOrderSummaryProps = {
  deliveryDate: string
  cutOffDate: string
  discountSofteningFreeDeliveryValue: string
}

type YourOrderSummaryHeaderProps = {
  deliveryDate: string
}

type YourOrderSummarySubHeaderProps = {
  cutOffDate: string
}

export const formattedDeliveryDate = (deliveryDate: string) =>
  moment(deliveryDate, 'YYYY-MM-DD').format('dddd, MMM D')

export const formattedCutOoffDate = (cutOffDate: string) =>
  moment(cutOffDate).format('dddd, Do of MMMM')

const CenteredAndClickableBox = withCitrusTheme(Box, () => ({
  margin: 'auto',
  cursor: 'pointer',
}))

const YourOrderSummaryHeader = ({ deliveryDate }: YourOrderSummaryHeaderProps) => (
  <Box>
    <Heading3>Your Order Summary</Heading3>
    <Body2>Arrives {formattedDeliveryDate(deliveryDate)}</Body2>
  </Box>
)

const YourOrderSummarySubHeader = ({ cutOffDate }: YourOrderSummarySubHeaderProps) => (
  <Box>
    <Heading5>Order details</Heading5>
    <Body2>
      You can make changes to this order from your account until {formattedCutOoffDate(cutOffDate)}.
    </Body2>
  </Box>
)

export const YourOrderSummary = ({
  deliveryDate,
  cutOffDate,
  discountSofteningFreeDeliveryValue,
}: YourOrderSummaryProps) => {
  const { trackUnfoldSummary } = useWelcomeTracking()
  const [isOrderSummaryExpanded, setOrderSummaryExpanded] = useState(false)

  const handleExpandClick = () => {
    setOrderSummaryExpanded(!isOrderSummaryExpanded)

    trackUnfoldSummary({ isOpen: !isOrderSummaryExpanded })
  }

  const renderedOrderSummary = (
    <OrderSummary
      discountSofteningFreeDeliveryValue={discountSofteningFreeDeliveryValue}
      isOrderConfirmation={false}
      onOrderConfirmationMobile={false}
      orderSummaryCollapsed={false}
    />
  )

  const renderTabletAndDesktopYourOrderSummary = () => (
    <Box display={['none', 'none', 'block', 'block']}>
      <Join with={<Space size={6} />}>
        <YourOrderSummaryHeader deliveryDate={deliveryDate} />
        <YourOrderSummarySubHeader cutOffDate={cutOffDate} />
      </Join>
      <Box>{renderedOrderSummary}</Box>
    </Box>
  )

  const renderMobileYourOrderSummary = () => (
    <>
      <Box
        display={['flex', 'flex', 'none', 'none']}
        alignItems={AlignItems.Center}
        justifyContent={JustifyContent.SpaceBetween}
      >
        <YourOrderSummaryHeader deliveryDate={deliveryDate} />
        <Box>
          <CenteredAndClickableBox
            role="button"
            aria-label="view order summary"
            onClick={handleExpandClick}
          >
            <Icon
              name={!isOrderSummaryExpanded ? 'chevron_down' : 'chevron_up'}
              variant={IconVariant.Actionable}
              size={6}
            />
          </CenteredAndClickableBox>
        </Box>
      </Box>
      {isOrderSummaryExpanded ? (
        <Box>
          <Space size={3} />
          <YourOrderSummarySubHeader cutOffDate={cutOffDate} />

          <Box>{renderedOrderSummary}</Box>
        </Box>
      ) : null}
    </>
  )

  return (
    <Box
      paddingH={[3, 5, 8, 8]}
      paddingV={[3, 5, 8, 8]}
      paddingRight={[3, 5, 8, 8]}
      bg={Color.White}
    >
      {renderTabletAndDesktopYourOrderSummary()}
      {renderMobileYourOrderSummary()}
    </Box>
  )
}
